import { useState } from "react";
import { Loader2, Eye, EyeOff } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BG_IMAGE = "/bg-login.png";

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Errore di accesso");
    }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore invio email");
    }
    setForgotLoading(false);
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Login Panel — centered */}
      <div className="relative z-10 w-full max-w-sm px-6">
        {/* Brand */}
        <div className="flex flex-col items-center mb-8">
          <img src="/logo-radar.webp" alt="Property Radar" className="w-20 h-20 object-contain drop-shadow-lg" />
          <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tighter leading-none mt-3">
            Property Radar
          </h1>
          <p className="text-xs text-white/60 tracking-wide mt-1 font-medium">by Stefano Tronca</p>
          <div className="h-px w-full max-w-xs bg-white/10 mt-3" />
        </div>

        {showForgot ? (
          forgotSent ? (
            <div className="space-y-4">
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-white font-semibold text-sm">Email inviata!</p>
                <p className="text-white/40 text-xs mt-1">Controlla la tua casella di posta.</p>
              </div>
              <button onClick={() => { setShowForgot(false); setForgotSent(false); }} className="w-full text-center text-white/50 hover:text-white text-sm transition-colors">
                Torna al login
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgot} className="space-y-4">
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-lg p-6 space-y-4">
                <div>
                  <h2 className="text-white font-bold text-base">Password dimenticata?</h2>
                  <p className="text-white/40 text-xs mt-1">Inserisci la tua email per ricevere il link di reset.</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-white/40">Email</label>
                  <input data-testid="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="nome@siacasa.it" className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 text-sm focus:outline-none focus:border-white/30 transition-all" required />
                </div>
                <button data-testid="forgot-submit" type="submit" disabled={forgotLoading} className="w-full h-11 bg-white text-zinc-900 font-bold text-sm rounded-lg hover:bg-white/90 transition-all active:scale-[0.98] flex items-center justify-center">
                  {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia link di reset"}
                </button>
              </div>
              <button type="button" onClick={() => setShowForgot(false)} className="w-full text-center text-white/50 hover:text-white text-sm transition-colors">
                Torna al login
              </button>
            </form>
          )
        ) : (
          <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-4">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-lg p-6 space-y-4">
              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-white/40">Email</label>
                <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@siacasa.it" className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 text-sm focus:outline-none focus:border-white/30 focus:bg-white/10 transition-all" required autoComplete="email" />
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-white/40">Password</label>
                  <button type="button" data-testid="forgot-password-link" onClick={() => setShowForgot(true)} className="text-[10px] text-white/30 hover:text-white/60 transition-colors">
                    Password dimenticata?
                  </button>
                </div>
                <div className="relative">
                  <input data-testid="login-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="w-full h-11 px-4 pr-11 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 text-sm focus:outline-none focus:border-white/30 focus:bg-white/10 transition-all" required autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors" tabIndex={-1}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div data-testid="login-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
                  {error}
                </div>
              )}

              {/* Submit */}
              <button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 bg-white text-zinc-900 font-bold text-sm rounded-lg hover:bg-white/90 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accedi"}
              </button>
            </div>

            <p className="text-center text-[10px] text-white/20">
              Accesso riservato al personale SIACASA
            </p>
          </form>
        )}
      </div>

    </div>
  );
}
